import Store from 'store/store';
import { getLiveUrlFromPageData, isPlaceholder } from 'utils/data';
import formatDateTime from 'utils/formatDateTime';
import * as jsonPointer from 'utils/jsonPointer';
import { mergeInto } from 'utils/merge';
import { getStudioSlug, getCurrentRegionCode } from 'utils/studioConfig';

const STORAGE_KEY = 'uni-cms-data';
// const getKey = name => `uni-cms-data-${name}`;

const isMoviePlaceholder = (data) => !data?.section_type && isPlaceholder(data);

export function getPreviewDataFromData(data) {
	// Convert data to plain mutable object (clone to ensure mutability)
	if (data && typeof data.toJS === 'function') {
		data = data.toJS();
	} else if (data && typeof data === 'object') {
		data = structuredClone(data);
	}

	let dataName = data?.slug || data?.content?.slug;
	const isPlaceholder = isMoviePlaceholder(data);
	if (isPlaceholder && Store.get().domains?.isUniversal) {
		dataName = 'movie';
	}
	if (!dataName) {
		throw new Error('Data must have a slug to be used for preview');
	}
	const content = data?.content || {};
	const result = {};

	// Mark placeholder content
	if (content && isPlaceholder) {
		content.isPlaceholder = true;
	}

	const isMovie = !data?.section_type;
	// For movies, merge navigation footer data and include navigation in preview data
	if (isMovie) {
		const navigationContent = Store.get().data?.navigation?.content;
		const globalFooter = navigationContent?.moviePageFooter || {};
		if (globalFooter.copyright || globalFooter.links) {
			if (!content.footer) content.footer = {};
			content.footer.copyright = mergeInto(globalFooter.copyright, content.footer.copyright);
			content.footer.links = mergeInto(globalFooter.links, content.footer.links);
		}

		// Hide future dates for ticketing and homeEnt if they haven't passed yet
		if (content.dates) {
			const today = formatDateTime(new Date(), 'yyyy-MM-dd');
			if (content.dates.ticketing && content.dates.ticketing > today) delete content.dates.ticketing;
			if (content.dates.homeEnt && content.dates.homeEnt > today) delete content.dates.homeEnt;
		}

		// Include full navigation data for Share Preview
		if (navigationContent) {
			result.navigation = navigationContent;
		}

		// Merge base_movie content into locale movie content for preview
		const mergedContent = mergeInto(data?.base_movie?.content, content);

		// Include movie metadata at root level
		result[dataName] = {
			...mergedContent,
			id: data.id,
			published_status: data.published_status,
			ready_state: data.ready_state,
			thundr_id: data.thundr_id,
		};
	} else {
		result[dataName] = content;
	}

	return result;
}

export function preparePreviewData(data) {
	try {
		// Get all preview data (movie/page data + navigation)
		// All data packaging is handled in getPreviewDataFromData
		const previewData = getPreviewDataFromData(data);

		localStorage.setItem(
			STORAGE_KEY,
			JSON.stringify({ at: Date.now(), data: previewData }),
		);
	} catch (err) {
		console.error(err);
	}
}

/** Gets prepared preview data by name
** @param {string} name - Name of the data section to retrieve (e.g. navigation). For movie pages, this should be the thundr ID.
*/
export async function getPreparedPreviewData(name) {
	try {
		const all = JSON.parse(localStorage.getItem(STORAGE_KEY));
		return all?.data?.[name] || Object.values(all?.data || {}).find(d => d?.thundr_id == name) || null;
	} catch {}
	return null;
}

export function getSimplePreviewPageLink(dataPath, mode = null) {
	const data = jsonPointer.get(Store.get().data, dataPath);
	const url = getLiveUrlFromPageData(data);
	const link = new URL('./preview/', location.href);

	// Add query params to the preview link
	link.searchParams.set('u', url || '/');
	link.searchParams.set('s', getStudioSlug());
	const regionCode = getCurrentRegionCode();
	if (regionCode) link.searchParams.set('r', regionCode);
	if (mode) link.searchParams.set(mode, '1');
	if (isMoviePlaceholder(data)) link.searchParams.set('placeholder', '1');

	// "/" do not need to be encoded in query string / hash - looks better when it's not
	return link.href.replace(/(?<=\?.+)%2F/g, '/');
}
